"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, UserCheck, Compass, MessageSquareCode, TrendingUp, ArrowRight } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
const pillars = [{
  icon: UserCheck,
  title: "Direct specialist delivery",
  description: "Every audit, technical specification, and strategic review is completed personally by me, maintaining technical continuity throughout the project."
}, {
  icon: Compass,
  title: "Tailored SEO strategy",
  description: "Every campaign is designed specifically for your industry, target audience, and website architecture rather than recycled agency templates."
}, {
  icon: MessageSquareCode,
  title: "Transparent recommendations",
  description: "I explain what needs fixing, why it matters, and the exact steps required to resolve it in clear, non-technical terms."
}, {
  icon: TrendingUp,
  title: "Sustainable search growth",
  description: "I focus on foundational SEO practices and search intent optimization that build durable organic visibility and real commercial value."
}];
const highlights = ["Hands-on technical and content implementation", "Clear prioritization focused on revenue-driving terms", "Honest advice with no inflated expectations or jargon", "Flexible collaboration matched to your internal workflow"];
export default function WhyConsultancy() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="whyyeconsultancy" data-nav="light" className="relative bg-background py-20 lg:py-28 overflow-hidden text-foreground">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Personal Narrative & Philosophy */}
          <motion.div initial={{
          opacity: 0,
          y: shouldReduceMotion ? 0 : 24
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          amount: 0.2
        }} transition={{
          duration: 0.6,
          ease: "easeOut"
        }} className="lg:col-span-6 space-y-6">
            <div className="inline-flex items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-medium tracking-wide uppercase">
                Independent SEO Consultancy
              </Badge>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight font-serif text-foreground">
              Why YE Consultancy
            </h2>

            <p className="text-lg text-muted-foreground leading-relaxed">
              I am Yassine Essaadi, an independent freelance SEO consultant. I founded YE Consultancy to offer businesses an authentic, senior-level alternative to large agency retainers and layered account management.
            </p>

            <div className="space-y-4 text-foreground/90 leading-relaxed">
              <p>
                When you partner with YE Consultancy, you collaborate with an independent{" "}
                <Link to="/FreelanceSeoConsultantPage" className="text-primary underline underline-offset-4 hover:text-primary/80 font-medium">
                  freelance SEO consultant
                </Link>
                {" "}who handles your strategy and technical roadmap firsthand. I take personal responsibility for diagnosing your organic search barriers, developing your strategy, and helping your team implement changes that hold up against algorithm shifts.
              </p>
              <p>
                I believe successful search marketing is grounded in thorough technical health, deep audience relevance, and clear business alignment. I do not hide behind vanity metrics or abstract reports—I focus on helping your business get found by customers who are ready to convert.
              </p>
              <p>
                Traditional SEO agencies serve a clear purpose—especially for larger organisations needing integrated multi-channel campaigns or high-volume delivery teams. Independent consultancy is not a universal replacement for that setup, but a focused alternative for businesses that specifically want direct senior expertise, agility, and hands-on accountability.
              </p>
            </div>

            {/* Bullet Highlights */}
            <div className="pt-2 space-y-3">
              {highlights.map((item, i) => <div key={i} data-index={i} className="flex items-start gap-3">
                  <CheckCircle2 className="size-5 text-primary shrink-0 mt-0.5" />
                  <span className="text-sm sm:text-base text-foreground font-medium">{item}</span>
                </div>)}
            </div>

            {/* CTAs */}
            <div className="pt-4 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/ContactPage">
                  Book a Free SEO Consultation
                  <ArrowRight className="size-4 ml-1" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/AboutMePage">
                  More About My Approach
                </Link>
              </Button>
            </div>
          </motion.div>

          {/* Right Column: Working Principles Cards */}
          <div className="lg:col-span-6 space-y-6">
            <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.15
          }} className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              {pillars.map((pillar, i) => {
              const IconComponent = pillar.icon;
              return <motion.div key={pillar.title} variants={itemVariants} data-index={i} className="h-full">
                    <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-[border-color,box-shadow] duration-200">
                      <CardContent className="p-6 flex flex-col h-full justify-between gap-4">
                        <div className="space-y-3">
                          <div className="size-11 rounded-lg bg-secondary flex items-center justify-center text-primary">
                            <IconComponent className="size-5" />
                          </div>
                          <h3 className="text-lg font-semibold font-serif text-foreground">
                            {pillar.title}
                          </h3>
                          <p className="text-sm text-muted-foreground leading-relaxed">
                            {pillar.description}
                          </p>
                        </div>
                      </CardContent>
                    </Card>
                  </motion.div>;
            })}
            </motion.div>

            {/* Direct Consultant Promise Box */}
            <motion.div initial={{
            opacity: 0,
            y: shouldReduceMotion ? 0 : 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true,
            amount: 0.2
          }} transition={{
            duration: 0.5,
            delay: 0.2,
            ease: "easeOut"
          }} className="rounded-xl border border-border bg-muted/60 p-6 sm:p-7 text-card-foreground">
              <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div className="space-y-1">
                  <span className="text-xs uppercase font-semibold text-primary tracking-wider">
                    My Commitment
                  </span>
                  <p className="text-sm sm:text-base font-medium text-foreground">
                    Hands-on SEO execution, clear technical priorities, and transparent reporting from day one.
                  </p>
                </div>
                <Button asChild variant="secondary" size="sm" className="shrink-0">
                  <Link to="/ServicesPage">
                    View All Services
                  </Link>
                </Button>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>;
}